import { useState } from 'react';
import { CarListing } from '../../types/dealership';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Car, ChevronLeft, ChevronRight } from 'lucide-react';
// Logo images
const imgLogoSayaara = "/Logos/Sayaara White Logo.png";

function ImageGallery({ images }: { images?: string[] }) {
  const [currentImage, setCurrentImage] = useState(0);
  
  // Use actual images if available, otherwise show placeholder
  const displayImages = images && images.length > 0 ? images : [];
  const hasImages = displayImages.length > 0;

  const nextImage = () => {
    if (hasImages) {
      setCurrentImage((prev) => (prev + 1) % displayImages.length);
    }
  };

  const prevImage = () => {
    if (hasImages) {
      setCurrentImage((prev) => (prev - 1 + displayImages.length) % displayImages.length);
    }
  };

  return (
    <Card>
      <CardContent className="p-0">
        <div className="relative">
          {hasImages ? (
            // Show actual car images
            <div className="w-full h-64 md:h-80 lg:h-96 relative">
              <img
                src={displayImages[currentImage]}
                alt={`${currentImage + 1} of ${displayImages.length}`}
                className="w-full h-full object-cover rounded-t-lg"
                onError={(e) => {
                  // Fallback to placeholder on error
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  const placeholder = target.nextElementSibling as HTMLDivElement;
                  if (placeholder) placeholder.style.display = 'flex';
                }}
              />
              {/* Error fallback placeholder (hidden by default) */}
              <div className="absolute inset-0 bg-gradient-to-br from-[#f91bdc]/10 to-[#f91bdc]/20 flex items-center justify-center rounded-t-lg" style={{ display: 'none' }}>
                <Car size={80} className="text-[#f91bdc]/60" />
              </div>
              
              {displayImages.length > 1 && (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white/90"
                    onClick={prevImage}
                  >
                    <ChevronLeft size={20} />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-white/80 hover:bg-white/90"
                    onClick={nextImage}
                  >
                    <ChevronRight size={20} />
                  </Button>
                  
                  <div className="absolute bottom-4 right-4 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
                    {currentImage + 1} / {displayImages.length}
                  </div>
                </>
              )}
            </div>
          ) : (
            // Show placeholder when no images
            <div className="w-full h-64 md:h-80 lg:h-96 bg-gradient-to-br from-[#f91bdc]/10 to-[#f91bdc]/20 flex items-center justify-center rounded-t-lg">
              <div className="text-[#f91bdc]/60 text-center">
                <Car size={80} className="mx-auto mb-2" />
                <p>No Images Available</p>
              </div>
            </div>
          )}
        </div>
        
        {hasImages && displayImages.length > 1 && (
          <div className="p-4">
            <div className="flex space-x-2 overflow-x-auto">
              {displayImages.map((image, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentImage(index)}
                  className={`flex-shrink-0 w-16 h-12 rounded border-2 transition-all duration-200 overflow-hidden ${
                    currentImage === index 
                      ? 'border-[#f91bdc]' 
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <img
                    src={image}
                    alt={`Thumbnail ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                </button>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

interface DetailedAdViewProps {
  listing: CarListing;
  onBackToListings: () => void;
  onEditListing: (listing: CarListing) => void;
}

export function DetailedAdView({ listing, onBackToListings, onEditListing }: DetailedAdViewProps) {
  const handleEditListing = () => {
    onEditListing(listing);
  };

  return (
    <div className="pt-20 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto pb-8">
      {/* Back Button */}
      <button 
        onClick={onBackToListings}
        className="flex items-center text-[#f91bdc] hover:text-[#e016c7] mb-6"
      >
        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Back to listings
      </button>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Image Carousel */}
          <ImageGallery images={listing.uploadedImageUrls} />

          {/* Car Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-2xl text-center text-[#f91bdc] border-b border-[#f91bdc] pb-2">
                {listing.carName}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <h3 className="font-semibold text-lg">Specifications:</h3>
                <div className={`grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 ${listing.vin ? 'lg:grid-cols-4' : 'lg:grid-cols-3'} gap-4`}>
                  <div className="flex items-start space-x-3 p-4 rounded-lg bg-gray-50">
                    <div className="text-[#f91bdc] flex-shrink-0 mt-1">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-600 mb-1">Year</p>
                      <p className="font-semibold text-sm">{listing.year || '2022'}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3 p-4 rounded-lg bg-gray-50">
                    <div className="text-[#f91bdc] flex-shrink-0 mt-1">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <circle cx="12" cy="12" r="10" strokeWidth={2}/>
                        <path d="M12 6v6l4 2" strokeWidth={2}/>
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-600 mb-1">Mileage</p>
                      <p className="font-semibold text-sm">{listing.mileage} km</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3 p-4 rounded-lg bg-gray-50">
                    <div className="text-[#f91bdc] flex-shrink-0 mt-1">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-600 mb-1">Transmission</p>
                      <p className="font-semibold text-sm">{listing.transmission.charAt(0).toUpperCase() + listing.transmission.slice(1)}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3 p-4 rounded-lg bg-gray-50">
                    <div className="text-[#f91bdc] flex-shrink-0 mt-1">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-600 mb-1">Fuel Type</p>
                      <p className="font-semibold text-sm">{listing.fuelType.charAt(0).toUpperCase() + listing.fuelType.slice(1)}</p>
                    </div>
                  </div>
                  
                  {listing.vin && (
                    <div className="flex items-start space-x-3 p-4 rounded-lg bg-gray-50">
                      <div className="text-[#f91bdc] flex-shrink-0 mt-1">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-600 mb-1">VIN</p>
                        <p className="font-semibold text-sm font-mono break-all">{listing.vin}</p>
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 pt-4 border-t">
                  <div className="flex items-start space-x-3 p-4 rounded-lg bg-gray-50">
                    <div className="text-[#f91bdc] flex-shrink-0 mt-1">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-600 mb-1">Drivetrain</p>
                      <p className="font-semibold text-sm">{listing.drivetrain || 'FWD'}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3 p-4 rounded-lg bg-gray-50">
                    <div className="text-[#f91bdc] flex-shrink-0 mt-1">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-600 mb-1">Exterior Color</p>
                      <p className="font-semibold text-sm">{listing.exteriorColor}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3 p-4 rounded-lg bg-gray-50">
                    <div className="text-[#f91bdc] flex-shrink-0 mt-1">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-600 mb-1">Interior Color</p>
                      <p className="font-semibold text-sm">{listing.interiorColor}</p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Financing Details */}
          <Card>
            <CardHeader>
              <CardTitle className="text-xl text-center text-[#f91bdc] border-b border-[#f91bdc] pb-2">
                Murabaha (Shariah-Compliant Financing)
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <p><span className="font-medium">Vehicle Base Price:</span> {listing.vehicleBasePrice || listing.msrp}</p>
                  <p><span className="font-medium">Total Sales Price:</span> {listing.totalSalesPrice || listing.msrp}</p>
                  <p><span className="font-medium">Term Length:</span> {listing.termDuration} months</p>
                  <p><span className="font-medium">Monthly Payment:</span> ${listing.monthlyPayment}</p>
                </div>
                <div className="space-y-2">
                  <p><span className="font-medium">Taxes:</span> {listing.taxes || 'As applicable'}</p>
                  <p><span className="font-medium">Down Payment:</span> {listing.downPayment || 'To be determined'}</p>
                  <p><span className="font-medium">Total Due Immediately:</span> {listing.totalDueImmediately || 'Contact dealer'}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Pricing */}
          <Card className="bg-[#f91bdc]/10 border-[#f91bdc]">
            <CardContent className="p-6 text-center">
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">Monthly Price:</p>
                <p className="text-3xl font-bold text-[#f91bdc]">${listing.monthlyPayment}/month</p>
              </div>
              <div className="space-y-2 text-sm mb-6">
                <p><span className="font-medium">Term Length:</span> {listing.termDuration} months</p>
                <p><span className="font-medium">Min Credit Score:</span> {listing.minCreditScore}</p>
              </div>
              <Button 
                onClick={handleEditListing}
                className="w-full bg-[#f91bdc] hover:bg-[#e016c7] text-white py-3 text-lg font-semibold"
              >
                Edit Listing
              </Button>
            </CardContent>
          </Card>

          {/* Car Description */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Car Description</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700 leading-relaxed">{listing.carDescription}</p>
            </CardContent>
          </Card>

          {/* Dealer Information */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Dealer Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <p className="font-semibold">{listing.dealerInfo?.name || 'XYZ Toyota – Toronto'}</p>
              <p className="text-sm text-gray-600">{listing.dealerInfo?.address || '123 Main Street, Toronto, ON'}</p>
              <p className="text-sm text-gray-600">Hours: {listing.dealerInfo?.hours || 'Mon–Sat, 9am–6pm'}</p>
              <p className="text-sm text-gray-600">{listing.dealerInfo?.email || '[Email Address]'}</p>
              <p className="text-sm text-gray-600">{listing.dealerInfo?.phone || '[Phone Number]'}</p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Footer */}
      <footer className="mt-16 bg-[#f91bdc] text-white rounded-lg p-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div>
            <div 
              className="w-32 h-14 bg-center bg-cover bg-no-repeat mb-4"
              style={{ backgroundImage: `url('${imgLogoSayaara}')` }}
            />
          </div>
          <div>
            <h3 className="font-semibold mb-4">Company</h3>
            <ul className="space-y-2 text-sm">
              <li><button onClick={(e) => e.preventDefault()} className="hover:underline text-left">About Us</button></li>
              <li><button onClick={(e) => e.preventDefault()} className="hover:underline text-left">How It Works</button></li>
              <li><button onClick={(e) => e.preventDefault()} className="hover:underline text-left">Browse Cars</button></li>
            </ul>
          </div>
          <div>
            <h3 className="font-semibold mb-4">Legal</h3>
            <ul className="space-y-2 text-sm">
              <li><button onClick={(e) => e.preventDefault()} className="hover:underline text-left">Terms &amp; Conditions</button></li>
              <li><button onClick={(e) => e.preventDefault()} className="hover:underline text-left">Privacy Policy</button></li>
              <li><button onClick={(e) => e.preventDefault()} className="hover:underline text-left">FAQs</button></li>
            </ul>
          </div>
          <div>
            <h3 className="font-semibold mb-4">Contact Info</h3>
            <div className="space-y-2 text-sm">
              <p>+1 (437) 887-1015</p>
              <p>Contact@sayaara.com</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}